"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import {
  setGlobalColorTheme,
  setSecondaryTheme,
  setSidebarTheme,
} from "@/lib/utils";
import { Paintbrush, RefreshCcw } from "lucide-react";
import { useEffect, useState } from "react";

const STORAGE_KEY_PRIMARY = "theme-primary-color";
const STORAGE_KEY_SECONDARY = "theme-secondary-color"; // Tambahan Secondary
const STORAGE_KEY_SIDEBAR = "theme-sidebar-color";

const DEFAULT_PRIMARY = "#0040FF";
const DEFAULT_SECONDARY = "#F1F5F9b"; // Slate-500 sebagai default Secondary
const DEFAULT_SIDEBAR = "#ffffff";

export function ThemeCustomizer() {
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY); // State Secondary
  const [sidebarColor, setSidebarColor] = useState(DEFAULT_SIDEBAR);
  const [isMounted, setIsMounted] = useState(false);

  // 1. LOAD FROM STORAGE
  useEffect(() => {
    setIsMounted(true);

    const savedPrimary = localStorage.getItem(STORAGE_KEY_PRIMARY);
    const savedSecondary = localStorage.getItem(STORAGE_KEY_SECONDARY);
    const savedSidebar = localStorage.getItem(STORAGE_KEY_SIDEBAR);

    if (savedPrimary) {
      setPrimaryColor(savedPrimary);
      setGlobalColorTheme(savedPrimary);
    } else {
      setGlobalColorTheme(DEFAULT_PRIMARY);
    }

    // Load Secondary
    if (savedSecondary) {
      setSecondaryColor(savedSecondary);
      setSecondaryTheme(savedSecondary);
    } else {
      setSecondaryTheme(DEFAULT_SECONDARY);
    }

    if (savedSidebar) {
      setSidebarColor(savedSidebar);
      setSidebarTheme(savedSidebar);
    } else {
      setSidebarTheme(DEFAULT_SIDEBAR);
    }
  }, []);

  // 2. SAVE & APPLY PRIMARY
  useEffect(() => {
    if (isMounted) {
      setGlobalColorTheme(primaryColor);
      localStorage.setItem(STORAGE_KEY_PRIMARY, primaryColor);
    }
  }, [primaryColor, isMounted]);

  // 3. SAVE & APPLY SECONDARY
  useEffect(() => {
    if (isMounted) {
      setSecondaryTheme(secondaryColor);
      localStorage.setItem(STORAGE_KEY_SECONDARY, secondaryColor);
    }
  }, [secondaryColor, isMounted]);

  // 4. SAVE & APPLY SIDEBAR
  useEffect(() => {
    if (isMounted) {
      setSidebarTheme(sidebarColor);
      localStorage.setItem(STORAGE_KEY_SIDEBAR, sidebarColor);
    }
  }, [sidebarColor, isMounted]);

  const resetTheme = () => {
    setPrimaryColor(DEFAULT_PRIMARY);
    setSecondaryColor(DEFAULT_SECONDARY);
    setSidebarColor(DEFAULT_SIDEBAR);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full">
          <Paintbrush className="h-4 w-4" />
          Theme
        </Button>
      </PopoverTrigger>
      {/* Diperlebar sedikit jika butuh ruang scroll, atau biarkan statis jika muat */}
      <PopoverContent className="w-80 max-h-[80vh] overflow-y-auto">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h4 className="font-medium leading-none">Theme Customizer</h4>
              <p className="text-xs text-muted-foreground">
                Customize layout colors
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={resetTheme}
              title="Reset"
            >
              <RefreshCcw className="h-3 w-3" />
            </Button>
          </div>

          <Separator />

          {/* --- SECTION 1: PRIMARY COLOR --- */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">
              Brand Color (Primary)
            </Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-10 h-8 p-0 border-0 cursor-pointer rounded overflow-hidden"
              />
              <Input
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="flex-1 h-8 text-xs uppercase"
                maxLength={7}
              />
            </div>
            {/* Quick Presets Primary */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {["#0f172a", "#0040FF", "#ef4444", "#22c55e", "#f59e0b"].map(
                (color) => (
                  <button
                    key={color}
                    className="w-5 h-5 rounded-full border border-border shadow-sm hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    onClick={() => setPrimaryColor(color)}
                  />
                ),
              )}
            </div>
          </div>

          <Separator />

          {/* --- SECTION 2: SECONDARY COLOR --- */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">
              Accent Color (Secondary)
            </Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                className="w-10 h-8 p-0 border-0 cursor-pointer rounded overflow-hidden"
              />
              <Input
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                className="flex-1 h-8 text-xs uppercase"
                maxLength={7}
              />
            </div>
            {/* Quick Presets Secondary */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {["#64748b", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"].map(
                (color) => (
                  <button
                    key={color}
                    className="w-5 h-5 rounded-full border border-border shadow-sm hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    onClick={() => setSecondaryColor(color)}
                  />
                ),
              )}
            </div>
          </div>

          <Separator />

          {/* --- SECTION 3: SIDEBAR COLOR --- */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Sidebar Background</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={sidebarColor}
                onChange={(e) => setSidebarColor(e.target.value)}
                className="w-10 h-8 p-0 border-0 cursor-pointer rounded overflow-hidden"
              />
              <Input
                value={sidebarColor}
                onChange={(e) => setSidebarColor(e.target.value)}
                className="flex-1 h-8 text-xs uppercase"
                maxLength={7}
              />
            </div>
            {/* Quick Presets Sidebar (Light & Dark options) */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {[
                "#ffffff", // White
                "#f8fafc", // Slate-50 (Light)
                "#0f172a", // Slate-900 (Dark)
                "#1e293b", // Slate-800
                "#18181b", // Zinc-950
              ].map((color) => (
                <button
                  key={color}
                  className="w-5 h-5 rounded-full border border-border shadow-sm hover:scale-110 transition-transform"
                  style={{ backgroundColor: color }}
                  onClick={() => setSidebarColor(color)}
                />
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
